import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import api from "../lib/api";
import { GoldenFiveSurvey } from "../components/GoldenFiveSurvey";
import { LessonQuiz } from "../components/LessonQuiz";
import { PreflightInspection } from "../components/PreflightInspection";
import { LessonVideo } from "../components/LessonVideo";
import { getIcon } from "../lib/icons";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Circle, Clock, ClipboardCheck, Loader2, ChevronRight, Lock,
  Target, ListChecks, Scale, FileDown, Link2, ShieldAlert, Hourglass, Rocket, Crosshair, Lightbulb, Compass, ThumbsUp, ThumbsDown,
  ChevronDown, Landmark, BookMarked, ExternalLink,
} from "lucide-react";

function renderMd(text) {
  return (text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>');
}

export default function ModuleView() {
  const { moduleId } = useParams();
  const nav = useNavigate();
  const [mod, setMod] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [quizScores, setQuizScores] = useState({});
  const [lessonQuizScores, setLessonQuizScores] = useState({});
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [badgeDefs, setBadgeDefs] = useState([]);
  const [missionOneCelebrate, setMissionOneCelebrate] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [clarity, setClarity] = useState(null);
  const [showSurvey, setShowSurvey] = useState(false);
  const [refsOpen, setRefsOpen] = useState(false);
  const startRef = useRef(Date.now());

  const load = () => {
    Promise.all([api.get(`/modules/${moduleId}`), api.get("/progress"), api.get("/badges")])
      .then(([m, p, b]) => {
        setMod(m.data);
        setCompleted(p.data.completed_lessons || []);
        setQuizScores(p.data.quiz_scores || {});
        setLessonQuizScores(p.data.lesson_quiz_scores || {});
        setEarnedBadges(p.data.earned_badges || []);
        setBadgeDefs(b.data || []);
      })
      .catch((e) => {
        if (e.response?.status === 402) nav("/checkout");
        else nav("/dashboard");
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [moduleId]);

  useEffect(() => {
    startRef.current = Date.now();
    setClarity(null);
    const l = mod?.lessons?.[active];
    if (l && !l.coming_soon) {
      api.post("/lessons/start", { module_id: moduleId, lesson_id: l.id }).catch(() => {});
    }
  }, [active, moduleId, mod]);

  useEffect(() => {
    if (moduleId === "gs-welcome" && mod && completed.length && !localStorage.getItem("da_gf_survey_done")) {
      const allDone = mod.lessons.every((l) => completed.includes(l.id));
      if (allDone) setShowSurvey(true);
    }
  }, [completed, mod, moduleId]);

  if (loading || !mod)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );

  const lesson = mod.lessons[active];
  const isDone = completed.includes(lesson.id);
  const doneCount = mod.lessons.filter((l) => completed.includes(l.id)).length;
  const pct = Math.round((doneCount / mod.lessons.length) * 100);
  const comingSoon = !!lesson.coming_soon || (!lesson.content && !lesson.sections);
  const academy = mod.academy;
  const hasQuiz = !!mod.has_module_quiz;
  const nextLesson = mod.lessons[active + 1];
  const nextTitle = nextLesson ? nextLesson.title : (hasQuiz ? "Mission Check" : "Module Complete");
  const nextTime = nextLesson?.duration ? `${nextLesson.duration} min` : "5 min";
  const moduleQuiz = quizScores[moduleId];
  const quizLessonIds = mod.lesson_quiz_ids || [];
  const lessonHasQuiz = quizLessonIds.includes(lesson.id);
  const isUnlocked = (i) => i === 0 || completed.includes(mod.lessons[i - 1].id);

  const handleQuizPassed = (result) => {
    const firstTime = !completed.includes(lesson.id);
    setCompleted((c) => [...new Set([...c, lesson.id])]);
    if (result) {
      setLessonQuizScores((s) => ({
        ...s,
        [lesson.id]: {
          ...(s[lesson.id] || {}),
          percent: result.percent,
          best_percent: Math.max((s[lesson.id] && s[lesson.id].best_percent) || 0, result.percent),
          passed: true,
        },
      }));
    }
    const seconds = Math.max(0, Math.round((Date.now() - startRef.current) / 1000));
    api.post("/lessons/feedback", { module_id: moduleId, lesson_id: lesson.id, seconds }).catch(() => {});
    api.get("/progress").then((p) => { setQuizScores(p.data.quiz_scores || {}); setLessonQuizScores(p.data.lesson_quiz_scores || {}); }).catch(() => {});
    if (result?.earned_badges) {
      const fresh = result.earned_badges.filter((b) => !earnedBadges.includes(b));
      fresh.forEach((bid) => {
        const def = badgeDefs.find((d) => d.id === bid);
        if (def) toast.success(`Badge unlocked — ${def.name}`, { description: def.desc, duration: 6000 });
      });
      setEarnedBadges(result.earned_badges);
    }
    if (firstTime && lesson.id === "gsw-1") {
      setMissionOneCelebrate(true);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goNext = () => {
    if (nextLesson) {
      setActive(active + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const markComplete = async () => {
    setMarking(true);
    await api.post("/progress/complete-lesson", { module_id: moduleId, lesson_id: lesson.id });
    const seconds = Math.max(0, Math.round((Date.now() - startRef.current) / 1000));
    api.post("/lessons/feedback", { module_id: moduleId, lesson_id: lesson.id, seconds }).catch(() => {});
    setMarking(false);
    setCompleted((c) => [...new Set([...c, lesson.id])]);
    toast.success("Mission complete — see your debrief below.");
  };

  const sendClarity = (val) => {
    setClarity(val);
    api.post("/lessons/feedback", { module_id: moduleId, lesson_id: lesson.id, clear: val }).catch(() => {});
    toast.success("Thanks for the feedback.");
  };

  return (
    <div className="min-h-screen pb-16">
      <Navbar />

      {missionOneCelebrate && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
          data-testid="mission-one-celebration" onClick={() => setMissionOneCelebrate(false)}>
          <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
            className="relative max-w-md w-full p-8 rounded-sm border border-primary/50 bg-card text-center glow-cyan"
            onClick={(e) => e.stopPropagation()}>
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/15 flex items-center justify-center">
              <Rocket className="h-8 w-8 text-primary" />
            </div>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Mission One Complete</span>
            <h3 className="font-display text-2xl font-bold mt-2">You're officially airborne. ✈</h3>
            <p className="text-sm text-slate-400 mt-2">
              First mission down — you've earned your <span className="text-primary font-medium">First Flight</span> badge.
              You're now <span className="text-primary font-medium">1 of 5</span> toward Golden Five Graduate.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 mt-6">
              {nextLesson && (
                <Button onClick={() => { setMissionOneCelebrate(false); goNext(); }}
                  className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="mission-one-continue">
                  Continue to Lesson 2 <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              )}
              <Button variant="outline" onClick={() => setMissionOneCelebrate(false)}
                className="rounded-sm" data-testid="mission-one-close">Not now</Button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-5 flex-wrap" data-testid="lesson-breadcrumb">
          <Link to="/dashboard" className="hover:text-foreground">Academies</Link>
          {academy && (
            <>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to={`/academy/${academy.slug}`} className="hover:text-foreground">{academy.title}</Link>
            </>
          )}
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground">{mod.title}</span>
        </div>

        <div className="mb-6">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">{mod.level}</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-2">{mod.title}</h1>
          {mod.objective && <p className="text-sm text-slate-400 mt-2 max-w-2xl">{mod.objective}</p>}
          <div className="flex items-center gap-4 mt-4 max-w-md">
            <Progress value={pct} className="h-2 bg-secondary" />
            <span className="font-mono text-sm text-primary shrink-0">{doneCount}/{mod.lessons.length}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4 space-y-2">
            {mod.lessons.map((l, i) => {
              const d = completed.includes(l.id);
              const unlocked = isUnlocked(i);
              if (!unlocked) {
                return (
                  <button key={l.id} data-testid={`lesson-item-${i}`} disabled
                    onClick={() => toast.info("Pass the previous lesson's knowledge check to unlock this one.")}
                    className="w-full text-left p-4 rounded-sm border border-border/50 bg-card/50 flex items-start gap-3 opacity-60 cursor-not-allowed">
                    <Lock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-medium text-sm text-muted-foreground">{l.title}</div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 font-mono">
                        <Lock className="h-3 w-3" /> Locked
                      </div>
                    </div>
                  </button>
                );
              }
              return (
                <button key={l.id} onClick={() => setActive(i)} data-testid={`lesson-item-${i}`}
                  className={`w-full text-left p-4 rounded-sm border transition-colors flex items-start gap-3 ${
                    i === active ? "border-primary/60 bg-primary/5" : "border-border/70 bg-card hover:border-border"}`}>
                  {d ? <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                     : <Circle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className="font-medium text-sm">{l.title}</div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 font-mono">
                      {l.coming_soon ? <><Hourglass className="h-3 w-3" /> Coming soon</>
                        : <><Clock className="h-3 w-3" /> {l.duration || 5} min</>}
                    </div>
                  </div>
                </button>
              );
            })}
            {hasQuiz && (
              <Link to={`/quiz/${moduleId}`} className="block" data-testid="take-quiz-link">
                <div className="p-4 rounded-sm border border-accent/40 bg-accent/5 hover:bg-accent/10 transition-colors flex items-center gap-3">
                  <ClipboardCheck className="h-5 w-5 text-accent shrink-0" />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-accent">Mission Check</div>
                    <div className="text-xs text-muted-foreground font-mono">Test your knowledge</div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-accent" />
                </div>
              </Link>
            )}
          </aside>

          <section className="lg:col-span-8">
            <motion.article key={lesson.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="p-6 md:p-8 rounded-sm border border-border/70 bg-card" data-testid="lesson-content">

              {lesson.graphic && (
                <div className="relative -m-6 md:-m-8 mb-6 md:mb-8 overflow-hidden border-b border-border/60 bg-black" data-testid="lesson-graphic">
                  <img
                    src={lesson.graphic.url}
                    alt={lesson.graphic.alt || lesson.title}
                    className="w-full h-auto block"
                  />
                </div>
              )}

              <div className="flex items-center justify-between mb-5">
                <h2 className="font-display text-2xl font-bold">{lesson.title}</h2>
                {isDone && <span className="text-xs font-mono uppercase tracking-widest text-primary flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Done</span>}
              </div>

              {lesson.cold_open?.quote?.length > 0 && (
                <div className="mb-6" data-testid="lesson-cold-open">
                  {lesson.cold_open.quote.map((line, idx) => (
                    <p key={idx} className="font-display text-lg md:text-xl text-slate-200 leading-relaxed mb-1.5">{line}</p>
                  ))}
                </div>
              )}

              <LessonVideo video={lesson.instructional_video} title={lesson.title} />

              {lesson.references?.badge && (
                <div className="mb-5 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-accent border border-accent/40 bg-accent/10 rounded-sm px-2.5 py-1" data-testid="lesson-regulatory-badge">
                  <Landmark className="h-3 w-3" /> {lesson.references.badge}
                </div>
              )}

              {lesson.mission_brief && (
                <div className="mb-6 rounded-sm border border-primary/30 bg-primary/5 overflow-hidden" data-testid="lesson-mission-brief">
                  <div className="flex items-center gap-2 px-5 py-2.5 border-b border-primary/20 bg-primary/10">
                    <Crosshair className="h-4 w-4 text-primary" />
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Mission Brief</span>
                  </div>
                  <div className="p-5">
                    <div className="font-display font-bold text-lg mb-4">{lesson.mission_brief.mission}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-1">
                      {[
                        ["Est. Time", lesson.mission_brief.time],
                        ["Difficulty", lesson.mission_brief.difficulty],
                        ["Prerequisites", lesson.mission_brief.prerequisites],
                        ["Equipment", lesson.mission_brief.equipment],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label}>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
                          <div className="text-sm font-medium mt-0.5">{value}</div>
                        </div>
                      ))}
                    </div>
                    {lesson.mission_brief.objective && (
                      <div className="pt-4 mt-3 border-t border-primary/15">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-primary mb-1">Mission Objective</div>
                        <p className="text-[15px] text-slate-200 leading-relaxed">{lesson.mission_brief.objective}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {lesson.mission && (
                <p className="mb-6 text-[15px] text-slate-300 leading-relaxed border-l-2 border-accent/50 pl-4 italic" data-testid="lesson-mission">
                  {lesson.mission}
                </p>
              )}

              {lesson.objectives?.length > 0 && (
                <div className="mb-6 p-4 rounded-sm border border-primary/25 bg-primary/5" data-testid="lesson-objectives">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-2">
                    <Target className="h-4 w-4" /> Learning Objectives
                  </div>
                  <ul className="space-y-1.5 text-sm text-slate-300">
                    {lesson.objectives.map((o, idx) => <li key={idx} className="flex gap-2"><span className="text-primary">›</span>{o}</li>)}
                  </ul>
                </div>
              )}

              {lesson.scenario?.prompt && (
                <div className="mb-6 p-5 rounded-sm border border-accent/30 bg-accent/5" data-testid="lesson-scenario">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-accent mb-2">
                    <Compass className="h-4 w-4" /> Scenario
                  </div>
                  <p className="text-[15px] text-slate-200 leading-relaxed italic">{lesson.scenario.prompt}</p>
                  <p className="text-xs text-muted-foreground mt-2">Hold this in mind as you work through the lesson.</p>
                </div>
              )}

              {comingSoon ? (
                <div className="py-10 text-center border border-dashed border-border rounded-sm" data-testid="lesson-coming-soon">
                  <Hourglass className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                  <p className="font-display font-bold text-lg">Lesson content coming soon</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    This lesson is part of the frozen Ground School v1.0 curriculum. Original, FAA-referenced
                    content is being produced and will appear here.
                  </p>
                </div>
              ) : (
                <>
                  {lesson.content && (
                    <div className="prose-content space-y-4 text-slate-300 leading-relaxed">
                      {lesson.content.split("\n\n").map((para, idx) => (
                        <p key={idx} className="text-[15px] whitespace-pre-line" dangerouslySetInnerHTML={{ __html: renderMd(para) }} />
                      ))}
                    </div>
                  )}
                  {lesson.sections?.map((sec, idx) => (
                    <div key={idx} className="mt-6">
                      {sec.heading && <h3 className="font-display font-bold text-lg mb-2">{sec.heading}</h3>}
                      <div className="text-[15px] text-slate-300 leading-relaxed whitespace-pre-line"
                        dangerouslySetInnerHTML={{ __html: renderMd(sec.body_markdown) }} />
                    </div>
                  ))}
                  {lesson.key_takeaways?.length > 0 && (
                    <div className="mt-6 p-4 rounded-sm border border-border/60 bg-secondary/30" data-testid="lesson-takeaways">
                      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-2">
                        <ListChecks className="h-4 w-4" /> Key Takeaways
                      </div>
                      <ul className="space-y-1.5 text-sm text-slate-300">
                        {lesson.key_takeaways.map((k, idx) => <li key={idx} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />{k}</li>)}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {lesson.scenario?.resolution && (
                <div className="mt-6 rounded-sm border border-primary/30 bg-primary/5 overflow-hidden" data-testid="lesson-scenario-resolution">
                  <div className="flex items-center gap-2 px-5 py-2.5 border-b border-primary/20 bg-primary/10">
                    <Compass className="h-4 w-4 text-primary" />
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">How a Pilot Thinks It Through</span>
                  </div>
                  <div className="p-5">
                    {lesson.scenario.questions?.length > 0 && (
                      <ul className="space-y-1.5 text-sm text-slate-300 mb-3">
                        {lesson.scenario.questions.map((q, idx) => (
                          <li key={idx} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />{q}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-[15px] text-slate-200 leading-relaxed">{lesson.scenario.resolution}</p>
                  </div>
                </div>
              )}

              {lesson.pilot_tips?.length > 0 && lesson.pilot_tips.map((tip, idx) => (
                <div key={idx} className="mt-6 flex items-start gap-3 p-4 rounded-sm border border-accent/30 bg-accent/5" data-testid={`pilot-tip-${idx}`}>
                  <Lightbulb className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-1">Pilot Tip</div>
                    <p className="text-sm text-slate-300 leading-relaxed">{tip}</p>
                  </div>
                </div>
              ))}

              {lesson.practical_mission && (
                <div className="mt-6 p-5 rounded-sm border border-primary/25 bg-primary/5" data-testid="lesson-practical-mission">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-2">
                    <Rocket className="h-4 w-4" /> Practical Mission
                  </div>
                  <p className="font-display font-bold text-base">{lesson.practical_mission.title}</p>
                  {lesson.practical_mission.objective && <p className="text-sm text-slate-300 mt-1">{lesson.practical_mission.objective}</p>}
                  {lesson.practical_mission.instructions?.length > 0 && (
                    <ol className="list-decimal list-inside text-sm text-slate-300 mt-3 space-y-1">
                      {lesson.practical_mission.instructions.map((s, idx) => <li key={idx}>{s}</li>)}
                    </ol>
                  )}
                  {lesson.practical_mission.safety && (
                    <p className="flex items-start gap-2 text-xs text-accent mt-3"><ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />{lesson.practical_mission.safety}</p>
                  )}
                  {lesson.practical_mission.success && (
                    <p className="text-xs text-primary mt-3"><span className="font-mono uppercase tracking-widest">Success:</span> {lesson.practical_mission.success}</p>
                  )}
                </div>
              )}

              {lesson.reflection?.length > 0 && (
                <div className="mt-6 p-4 rounded-sm border border-border/60 bg-secondary/30" data-testid="lesson-reflection">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary mb-2">
                    <ListChecks className="h-4 w-4" /> Reflect
                  </div>
                  <ul className="space-y-1.5 text-sm text-slate-300 list-disc list-inside">
                    {lesson.reflection.map((q, idx) => <li key={idx}>{q}</li>)}
                  </ul>
                </div>
              )}

              {lesson.id === "gsw-4" && <PreflightInspection />}

              {!lesson.references && lesson.faa_references?.length > 0 && (
                <div className="mt-6 p-4 rounded-sm border border-accent/30 bg-accent/5" data-testid="lesson-faa-refs">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-accent mb-2">
                    <Scale className="h-4 w-4" /> FAA References
                  </div>
                  <ul className="space-y-2 text-sm">
                    {lesson.faa_references.map((r, idx) => (
                      <li key={idx}>
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-accent hover:underline font-medium">{r.title}</a>
                        {r.citation && <span className="text-slate-400"> — {r.citation}</span>}
                        {r.source_revision_date && <span className="text-xs text-muted-foreground font-mono block">Source rev: {r.source_revision_date}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!lesson.references && !lesson.faa_references?.length && lesson.faa_note && (
                <div className="mt-6 p-4 rounded-sm border border-border/60 bg-card" data-testid="lesson-faa-note">
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-accent mb-2">
                    <Scale className="h-4 w-4" /> FAA References
                  </div>
                  <p className="text-sm text-slate-400">{lesson.faa_note}</p>
                </div>
              )}

              {(lesson.workbook || lesson.resources?.length > 0) && (
                <div className="mt-6 flex flex-wrap gap-3" data-testid="lesson-resources">
                  {lesson.workbook && (
                    <a href={lesson.workbook.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-sm border border-border/70 bg-card hover:border-primary/50 transition-colors">
                      <FileDown className="h-4 w-4 text-primary" /> {lesson.workbook.label || "Pilot Logbook Page"}
                    </a>
                  )}
                  {lesson.resources?.map((r, idx) => (
                    <a key={idx} href={r.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-sm border border-border/70 bg-card hover:border-primary/50 transition-colors">
                      <Link2 className="h-4 w-4 text-primary" /> {r.label}
                    </a>
                  ))}
                </div>
              )}

              {lesson.references && (lesson.references.regulatory?.length || lesson.references.faa_aim?.length || lesson.references.faa_source?.length) && (
                <div className="mt-6 rounded-sm border border-border/60 bg-secondary/20 overflow-hidden" data-testid="lesson-references">
                  <button
                    type="button"
                    onClick={() => setRefsOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-secondary/40 transition-colors"
                    data-testid="lesson-references-toggle"
                    aria-expanded={refsOpen}
                  >
                    <span className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-300">
                      <BookMarked className="h-4 w-4 text-accent" /> Regulatory &amp; FAA References
                    </span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${refsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {refsOpen && (
                    <div className="px-4 pb-4 pt-1 space-y-4 border-t border-border/50">
                      {lesson.references.regulatory?.length > 0 && (
                        <div data-testid="lesson-references-regulatory">
                          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary mt-3 mb-1.5">
                            <Landmark className="h-3.5 w-3.5" /> Regulatory Reference · Primary authority
                          </div>
                          <ul className="space-y-1.5 text-sm text-slate-300">
                            {lesson.references.regulatory.map((r, idx) => (
                              <li key={idx} className="flex gap-2">
                                <span className="text-primary shrink-0">›</span>
                                <span><span className="font-semibold text-foreground">{r.cite}</span>{r.title ? ` — ${r.title}` : ""}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {lesson.references.faa_aim?.length > 0 && (
                        <div data-testid="lesson-references-faaaim">
                          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-accent mb-1.5">
                            <Scale className="h-3.5 w-3.5" /> FAA / FAR-AIM Reference · Supporting guidance
                          </div>
                          <ul className="space-y-1.5 text-sm text-slate-300">
                            {lesson.references.faa_aim.map((r, idx) => (
                              <li key={idx} className="flex gap-2"><span className="text-accent shrink-0">›</span><span>{r.source}</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {lesson.references.faa_source?.length > 0 && (
                        <div data-testid="lesson-references-source">
                          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">
                            <ExternalLink className="h-3.5 w-3.5" /> FAA.gov · Additional official explanation
                          </div>
                          <ul className="space-y-1.5 text-sm">
                            {lesson.references.faa_source.map((r, idx) => (
                              <li key={idx}>
                                <a href={r.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">{r.label}</a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground pt-1 border-t border-border/40 mt-2 leading-relaxed">
                        Regulations and FAA guidance can change. Always verify current requirements at faa.gov before operating.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!comingSoon && lessonHasQuiz && !isDone && (
                <LessonQuiz lessonId={lesson.id} onPassed={handleQuizPassed} />
              )}

              {!comingSoon && !lessonHasQuiz && (
                <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-border/60">
                  <Button onClick={markComplete} disabled={marking} data-testid="mark-complete-btn"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                    {marking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isDone ? "Mission Complete ✓" : "Complete Mission"}
                  </Button>
                  {active < mod.lessons.length - 1 && (
                    <Button variant="outline" onClick={goNext} className="rounded-sm" data-testid="next-lesson-btn">
                      Skip <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}

              {isDone && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-8 rounded-sm border border-primary/30 bg-primary/5 overflow-hidden" data-testid="lesson-debrief">
                  <div className="flex items-center gap-2 px-5 py-2.5 border-b border-primary/20 bg-primary/10">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    <span className="text-xs font-mono uppercase tracking-[0.3em] text-primary">Mission Debrief</span>
                  </div>
                  <div className="p-5">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Mission Status</div>
                        <div className="text-sm font-bold text-primary mt-0.5">✅ Complete</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Objectives</div>
                        <div className="text-sm font-medium mt-0.5">{lesson.objectives?.length || 0} / {lesson.objectives?.length || 0}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Knowledge Check</div>
                        <div className="text-sm font-medium mt-0.5">{lessonHasQuiz ? (lessonQuizScores[lesson.id] ? `${lessonQuizScores[lesson.id].best_percent}%` : "Not taken") : (moduleQuiz ? `${moduleQuiz.percent}%` : "Not taken")}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Next · Est. Time</div>
                        <div className="text-sm font-medium mt-0.5">{nextTime}</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-wrap pt-4 border-t border-primary/15">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Recommended Next Mission</div>
                        <div className="font-display font-bold">{nextTitle}</div>
                      </div>
                      {nextLesson ? (
                        <Button onClick={goNext} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="debrief-next-btn">
                          Next Mission <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      ) : hasQuiz ? (
                        <Link to={`/quiz/${moduleId}`} data-testid="debrief-quiz-btn">
                          <Button className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm">Take the Mission Check</Button>
                        </Link>
                      ) : (
                        <Link to={academy ? `/academy/${academy.slug}` : "/dashboard"} data-testid="debrief-academy-btn">
                          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                            Module Complete <CheckCircle2 className="ml-1.5 h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                    <div className="mt-6 pt-4 border-t border-primary/15 flex flex-col items-center gap-2.5 text-center" data-testid="lesson-clarity">
                      <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Was this lesson clear?</span>
                      {clarity === null ? (
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => sendClarity(true)} data-testid="clarity-yes" className="rounded-sm">
                            <ThumbsUp className="h-4 w-4 mr-1.5" /> Yes
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => sendClarity(false)} data-testid="clarity-no" className="rounded-sm">
                            <ThumbsDown className="h-4 w-4 mr-1.5" /> No
                          </Button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-sm text-primary" data-testid="clarity-thanks">
                          <CheckCircle2 className="h-4 w-4" /> Thanks for the feedback
                        </span>
                      )}
                    </div>

                    <div className="mt-10 pt-8 border-t border-primary/15 flex flex-col items-center text-center px-4 pb-4">
                      <motion.p
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }}
                        className="font-display italic text-slate-100 text-2xl sm:text-3xl leading-relaxed max-w-xl"
                        data-testid="debrief-principle">
                        "{lesson.debrief_line || "Great pilots never stop learning."}"
                      </motion.p>
                      <motion.span
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.6 }}
                        className="text-[11px] font-mono uppercase tracking-[0.3em] text-muted-foreground mt-5">
                        — DronAcademy Principle
                      </motion.span>
                      <motion.div
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.6 }}
                        className="font-display font-black tracking-tight text-base mt-10">
                        <span className="text-primary">DRON</span><span className="text-white">ACADEMY</span>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              )}

              {mod.disclaimer && (
                <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground border-t border-border/50 pt-4" data-testid="legal-disclaimer">
                  <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-accent" />
                  <span>{mod.disclaimer}</span>
                </div>
              )}
            </motion.article>
          </section>
        </div>
      </main>
      <GoldenFiveSurvey open={showSurvey} onClose={() => setShowSurvey(false)} />
    </div>
  );
}
